import {
    Box,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    useTheme
} from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from '../../../contexts/UserContext';
import { getAgentLastCall } from '../../../tools/cacheService';
import { Callbacks } from '../../engagement/callback';

// ─── CallbackWorkspace ──────────────────────────────────────────────────────────

export function CallbackWorkspace({ agentId, agentSkillIds, campaigns: allCampaigns, onActiveContactChange, externalRefreshKey }) {
    const userContext = useContext(UserContext);
    const theme = useTheme();

    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [loadingSelection, setLoadingSelection] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);

    // Filter to callback-enabled campaigns matching agent's skills (memoized to prevent unnecessary re-renders)
    const campaigns = useMemo(() => allCampaigns.filter(
        (campaign) =>
            campaign.callbackConfigurationId &&
            (!agentSkillIds || agentSkillIds.length === 0 || agentSkillIds.includes(campaign.skillId)),
    ), [allCampaigns, agentSkillIds]);

    // Auto-select campaign when the list changes
    useEffect(() => {
        autoSelectCampaign();
    }, [campaigns.length, agentId]);

    // Propagate external refresh signal from parent (e.g. ScheduleCallbackDialog)
    useEffect(() => {
        if (externalRefreshKey) {
            setRefreshKey((k) => k + 1);
        }
    }, [externalRefreshKey]);

    // If the selected campaign is removed from the list, deselect it
    useEffect(() => {
        if (selectedCampaign && !campaigns.find((c) => c.id === selectedCampaign.id)) {
            setSelectedCampaign(null);
        }
    }, [campaigns, selectedCampaign]);

    async function autoSelectCampaign() {
        if (campaigns.length === 1) {
            setSelectedCampaign(campaigns[0]);
            setLoadingSelection(false);
        } else if (campaigns.length > 1 && !selectedCampaign) {
            // Use the agent's last active call to auto-select the matching campaign
            setLoadingSelection(true);
            try {
                const agentData = await getAgentLastCall(agentId, userContext.apiKey);
                if (agentData?.skillId) {
                    const matchingCampaign = campaigns.find((c) => c.skillId === agentData.skillId);
                    if (matchingCampaign) {
                        setSelectedCampaign(matchingCampaign);
                    }
                }
            } catch (e) {
                console.warn('Could not fetch last call for campaign auto-select', e);
            }
            setLoadingSelection(false);
        } else {
            setLoadingSelection(false);
        }
    }

    return (
        <Box
            sx={{ width: '100%', backgroundColor: theme.palette.background.default, minHeight: '100%' }}
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
        >
            <Box sx={{ maxWidth: '1200px', width: '100%' }} display="flex" flexDirection="column" alignItems="center" gap={2}>
                <Box sx={{ width: '100%', padding: '10px' }} display="flex" gap={2} alignItems="center">
                    <FormControl fullWidth>
                        <InputLabel>Callback Campaign</InputLabel>
                        <Select
                            label="Callback Campaign"
                            value={selectedCampaign ? selectedCampaign.id : ''}
                            onChange={(e) => {
                                const campaign = campaigns.find((c) => c.id === e.target.value);
                                setSelectedCampaign(campaign);
                            }}
                            disabled={campaigns.length === 0 || loadingSelection}
                        >
                            {campaigns.map((campaign) => (
                                <MenuItem key={campaign.id} value={campaign.id}>
                                    {campaign.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {selectedCampaign && (
                    <Box sx={{ width: '100%', px: '10px' }}>
                        <Callbacks
                            key={selectedCampaign.id}
                            agentId={agentId}
                            campaignId={selectedCampaign.id}
                            skillId={selectedCampaign.skillId}
                            campaignProfileId={selectedCampaign.campaignProfileId}
                            callbackConfigurationId={selectedCampaign.callbackConfigurationId}
                            onActiveContactChange={onActiveContactChange}
                            refreshKey={refreshKey}
                        />
                    </Box>
                )}

                {!selectedCampaign && !loadingSelection && (
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 4 }}>
                        {campaigns.length === 0
                            ? 'No callback campaigns available for your active skills'
                            : 'Select a callback campaign to get started'}
                    </Typography>
                )}

                {loadingSelection && <CircularProgress sx={{ mt: 4 }} />}
            </Box>
        </Box>
    );
}